import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/lib/supabase";
import { SEOHead } from "@/components/SEOHead";
import { Card } from "@/components/ui/card";
import { DollarSign, Package, ShoppingCart, TrendingUp } from "lucide-react";
import { useUserQuery, useAnalyticsQuery } from "@/hooks/useOptimizedQuery";
import { cacheKeys } from "@/lib/cache";

const Analytics = () => {
  const { user } = useAuth();

  // Get shop info with user-level caching
  const { data: shop } = useUserQuery(
    cacheKeys.myShop(user?.id || ""),
    async () => {
      if (!user) return null;
      console.log('🔄 Fetching shop info for analytics...');
      const { data } = await supabase
        .from("shops")
        .select("id, name")
        .eq("owner_id", user.id)
        .maybeSingle();
      return data;
    },
    {
      enabled: !!user,
      optimisticKey: "my_shop",
    }
  );

  // Get analytics data with background refresh for real-time updates
  const { data: analytics, isLoading, cacheHit } = useAnalyticsQuery(
    cacheKeys.shopAnalytics(shop?.id || ""),
    async () => {
      if (!shop?.id) return null;
      console.log('🔄 Fetching analytics data...');

      // Get total products
      const { count: productCount } = await supabase
        .from("products")
        .select("*", { count: "exact", head: true })
        .eq("shop_id", shop.id);

      // Get total orders and revenue
      const { data: orders } = await supabase
        .from("order_items")
        .select("qty, price_cents, created_at")
        .eq("shop_id", shop.id);

      const totalOrders = orders?.length || 0;
      const totalRevenue = orders?.reduce((sum, item) => sum + (item.qty * item.price_cents), 0) || 0;
      const totalItemsSold = orders?.reduce((sum, item) => sum + item.qty, 0) || 0;

      // Get recent orders for trend (last 30 days)
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      const recentOrders = orders?.filter(order => 
        new Date(order.created_at) >= thirtyDaysAgo
      ) || [];

      const recentRevenue = recentOrders.reduce((sum, item) => sum + (item.qty * item.price_cents), 0);

      return {
        productCount: productCount || 0,
        totalOrders,
        totalRevenue,
        totalItemsSold,
        recentRevenue,
        recentOrders: recentOrders.length,
      };
    },
    {
      enabled: !!shop?.id,
      backgroundRefresh: true, // Real-time analytics updates
      optimisticKey: "shop_analytics",
    }
  );

  // Show cache performance in development
  if (import.meta.env.DEV && cacheHit) {
    console.log('⚡ Analytics data loaded from cache');
  }

  if (!user) {
    return (
      <>
        <SEOHead
          title="Analytics Dashboard"
          description="View your shop's performance metrics and sales analytics on BahaMarket."
        />
        <main className="container mx-auto px-4 py-10">
          <div className="text-center space-y-6">
            <h1 className="text-3xl font-heading font-bold">Analytics Dashboard</h1>
            <p className="text-muted-foreground">Please log in to view your analytics.</p>
          </div>
        </main>
      </>
    );
  }

  if (!shop) {
    return (
      <>
        <SEOHead
          title="Analytics Dashboard"
          description="View your shop's performance metrics and sales analytics on BahaMarket."
        />
        <main className="container mx-auto px-4 py-10">
          <div className="text-center space-y-6">
            <h1 className="text-3xl font-heading font-bold">Analytics Dashboard</h1>
            <p className="text-muted-foreground">Create your shop first to view analytics.</p>
          </div>
        </main>
      </>
    );
  }

  if (isLoading) {
    return (
      <>
        <SEOHead
          title="Analytics Dashboard"
          description="View your shop's performance metrics and sales analytics on BahaMarket."
        />
        <main className="container mx-auto px-4 py-10">
          <div className="space-y-8">
            <div className="space-y-2">
              <div className="h-8 bg-muted rounded w-64 animate-pulse"></div>
              <div className="h-5 bg-muted rounded w-48 animate-pulse"></div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {Array.from({ length: 4 }).map((_, i) => (
                <Card key={i} className="p-6 animate-pulse">
                  <div className="flex items-center justify-between">
                    <div className="space-y-2">
                      <div className="h-4 bg-muted rounded w-24"></div>
                      <div className="h-8 bg-muted rounded w-16"></div>
                    </div>
                    <div className="h-8 w-8 bg-muted rounded"></div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </main>
      </>
    );
  }

  return (
    <>
      <SEOHead
        title="Analytics Dashboard"
        description="View your shop's performance metrics and sales analytics on BahaMarket."
      />
      <main className="container mx-auto px-4 py-10">
        <div className="space-y-8">
          <div className="space-y-2">
            <h1 className="text-3xl font-heading font-bold">Analytics Dashboard</h1>
            <p className="text-muted-foreground">Performance overview for {shop.name}</p>
          </div>

          {analytics && (
            <>
              {/* Key Metrics */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <Card className="p-6 bg-card border-border/40">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground font-medium">Total Revenue</p>
                      <p className="text-2xl font-bold text-foreground">
                        BSD {(analytics.totalRevenue / 100).toFixed(2)}
                      </p>
                    </div>
                    <div className="p-3 bg-muted/50 rounded-full">
                      <DollarSign className="h-6 w-6 text-foreground/70" />
                    </div>
                  </div>
                </Card>

                <Card className="p-6 bg-card border-border/40">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground font-medium">Total Orders</p>
                      <p className="text-2xl font-bold text-foreground">{analytics.totalOrders}</p>
                    </div>
                    <div className="p-3 bg-muted/50 rounded-full">
                      <ShoppingCart className="h-6 w-6 text-foreground/70" />
                    </div>
                  </div>
                </Card>

                <Card className="p-6 bg-card border-border/40">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground font-medium">Items Sold</p>
                      <p className="text-2xl font-bold text-foreground">{analytics.totalItemsSold}</p>
                    </div>
                    <div className="p-3 bg-muted/50 rounded-full">
                      <Package className="h-6 w-6 text-foreground/70" />
                    </div>
                  </div>
                </Card>

                <Card className="p-6 bg-card border-border/40">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground font-medium">Products Listed</p>
                      <p className="text-2xl font-bold text-foreground">{analytics.productCount}</p>
                    </div>
                    <div className="p-3 bg-muted/50 rounded-full">
                      <TrendingUp className="h-6 w-6 text-foreground/70" />
                    </div>
                  </div>
                </Card>
              </div>

              {/* Recent Performance */}
              <Card className="p-6 bg-card border-border/40">
                <h2 className="text-xl font-semibold mb-6">Recent Performance (Last 30 Days)</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="space-y-4">
                    <div className="flex justify-between items-center py-2 border-b border-border/40">
                      <span className="text-muted-foreground">Revenue (30 days)</span>
                      <span className="font-semibold">BSD {(analytics.recentRevenue / 100).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-border/40">
                      <span className="text-muted-foreground">Recent Orders</span>
                      <span className="font-semibold">{analytics.recentOrders}</span>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center py-2 border-b border-border/40">
                      <span className="text-muted-foreground">Average per order</span>
                      <span className="font-semibold">
                        BSD {analytics.totalOrders > 0 ? (analytics.totalRevenue / analytics.totalOrders / 100).toFixed(2) : "0.00"}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-border/40">
                      <span className="text-muted-foreground">Conversion rate</span>
                      <span className="font-semibold">
                        {analytics.productCount > 0 ? ((analytics.totalOrders / analytics.productCount) * 100).toFixed(1) : "0"}%
                      </span>
                    </div>
                  </div>
                </div>
              </Card>
            </>
          )}
        </div>
      </main>
    </>
  );
};

export default Analytics;